import { DEFAULT_TOOLS } from '../types/config';
/**
 * Highlight Mode - Text selection playback with accessibility toolbar
 * Shows context menu on text selection. Clicking "Listen" opens the toolbar
 * and plays the selected text — not the full page.
 */

import type { ReadingVox } from '../core/ReadingVox';
import { Toolbar } from '../ui/Toolbar';
import { ContextMenu } from '../ui/ContextMenu';
import { ToggleButton } from '../ui/ToggleButton';
import type { Dictionary } from '../features/Dictionary';
import type { Translation } from '../features/Translation';
import { loadExtras, EXTRAS_ARE_LAZY } from '../lazy';
import { ToolUsageTracker } from '../features/ToolUsageTracker';
import { ToolManager } from '../tools/ToolManager';
import { resolveTheme } from '../ui/ThemeManager';
import type { ResolvedTheme } from '../ui/ThemeManager';
import { readableSelectionText } from '../utils/textExtractor';

export class HighlightMode {
  private widget: ReadingVox;
  private toolbar: Toolbar;
  private toolManager: ToolManager;
  private usageTracker: ToolUsageTracker | null = null;
  private contextMenu: ContextMenu;
  private toggleButton: ToggleButton | null = null;
  private dictionary: Dictionary | null = null;
  private translation: Translation | null = null;
  private toolsPromise: Promise<void> | null = null;
  private theme: ResolvedTheme;
  private lastSelection: { text: string; range: Range | null } | null = null;
  /** Selection made inside a readable iframe: its rect in this page, and how to clear it */
  private frameSelection: { rect: { left: number; top: number; width: number; height: number }; clear: () => void } | null = null;
  private boundHandleSelection: (e: Event) => void;

  constructor(widget: ReadingVox) {
    this.widget = widget;
    const config = (widget as any).config;
    this.theme = resolveTheme(config?.theme);

    // Create ToolManager
    this.toolManager = new ToolManager((this.widget as any).config?.apiEndpoint);

    // Create Toolbar (replaces FloatingPlayer)
    this.toolbar = new Toolbar({
      callbacks: {
        onPlay: () => this.playCurrentSelection(),
        onPause: () => this.widget.pause(),
        onStop: () => this.widget.stop(),
        onSpeedChange: (speed) => this.widget.setSpeed(speed),
        onVoiceChange: (voice) => this.widget.setVoice(voice),
        onDictionary: (word) => this.showDictionaryAt(word, window.innerWidth / 2 - 200, window.innerHeight / 3),
        onTranslate: (text) => this.showTranslationAt(text, window.innerWidth / 2 - 200, window.innerHeight / 3),
        onLanguageChange: (code) => this.widget.setLanguage(code),
        onDownload: (onProgress) => this.widget.downloadAudio(onProgress),
        onSkip: (delta) => this.widget.skipSentence(delta),
        onSummarize: () => this.widget.summarize(),
        onSpeakSummary: (bullets) => { void this.widget.speakSummary(bullets); },
        onSeek: (percent) => this.widget.seek(percent),
        getProgress: () => this.widget.getProgress(),
        onFirstShow: () => { this.ensureTools(); },
        onClose: () => {
          this.widget.stop();
          this.toolbar.hide();
          if (this.toggleButton) this.toggleButton.setActive(false);
        },
      },
      toolManager: this.toolManager,
      theme: this.theme,
      position: config?.toolbarPosition || 'top',
      insetLeft: config?.toolbarInsetLeft,
      insetRight: config?.toolbarInsetRight,
      enabledTools: config?.enabledTools,
      keyboardShortcuts: config?.keyboardShortcuts !== false,
      ttsMode: config?.tts,
      hideVoiceSelector: !!config?.licenseKey,
    });

    // Set initial voice in dropdown
    if (config?.voice) {
      this.toolbar.setVoice(config.voice);
    }

    // Context menu for text selection actions
    this.contextMenu = new ContextMenu({
      theme: this.theme,
      onListen: () => this.playSelection(),
      onDictionary: (word) => this.lookupWord(word),
      onTranslate: () => this.translateSelection()
    });

    // Toggle button (FAB)
    if (config?.showToggle !== false) {
      this.toggleButton = new ToggleButton({
        position: config?.togglePosition || 'bottom-right',
        bottomOffset: config?.toggleBottomOffset,
        pulse: config?.pulse !== false,
        theme: this.theme,
        onToggle: (visible) => {
          if (visible) {
            this.toolbar.show();
          } else {
            this.widget.stop();
            this.toolbar.hide();
          }
        }
      });
    }

    this.boundHandleSelection = this.handleSelection.bind(this);
  }

  /**
   * Initialize highlight mode
   */
  async init(): Promise<void> {
    const config = (this.widget as any).config;

    // Initialize ToolManager
    this.toolManager.init();

    // Initialize tool usage tracking
    this.initUsageTracker(config);

    // Server feature flags (translation, branding) — don't block init on them
    this.widget.onCaption((caption) => this.toolbar.setCaption(caption));
    this.widget.whenFeatures().then((features) => {
      this.toolbar.setFeatures(features);
      this.contextMenu.setTranslateEnabled(features.selectionTranslation);
    });

    // Apply saved voice from persisted settings
    // (only a voice the visitor explicitly chose — otherwise config.voice / data-voice stands)
    this.toolManager.setDefaultVoice(config?.voice);
    const savedSettings = this.toolManager.getSettings();
    // Reading settings (auto-scroll, highlight mode/colors) — now and on every change
    this.widget.applyReadingSettings(savedSettings);
    this.toolManager.onSettingsChanged((settings) => this.widget.applyReadingSettings(settings));
    if (savedSettings.voice && savedSettings.voiceChosen) {
      this.widget.setVoice(savedSettings.voice);
      this.toolbar.setVoice(savedSettings.voice);
    }

    // Listen for voice changes from settings panel
    this.toolManager.onSettingsChanged((settings) => {
      if (settings.voice) {
        this.widget.setVoice(settings.voice);
      }
    });

    // Register tools
    // Website build: tools load on first toolbar open; extension: register now
    if (!EXTRAS_ARE_LAZY) await this.ensureTools();

    if (config?.alwaysShowPlayer) {
      this.toolbar.show();
      if (this.toggleButton) this.toggleButton.hide();
    }

    // Listen for text selection
    document.addEventListener('mouseup', this.boundHandleSelection);
    document.addEventListener('touchend', this.boundHandleSelection);
    document.addEventListener('keyup', this.boundHandleSelection);

    // Hide menu on click outside
    document.addEventListener('mousedown', (e) => {
      if (!this.contextMenu.contains(e.target as Node)) {
        this.contextMenu.hide();
      }
    });
  }

  private initUsageTracker(config: any): void {
    const authHeaders: Record<string, string> = {};
    if (config?.apiKey) authHeaders['X-API-Key'] = config.apiKey;
    if (config?.licenseKey) authHeaders['X-License-Key'] = config.licenseKey;

    if (Object.keys(authHeaders).length > 0) {
      this.usageTracker = new ToolUsageTracker({
        apiEndpoint: config?.apiEndpoint || '/api/v1',
        authHeaders,
        pageUrl: () => this.widget.getPageUrl(),
        sessionId: `highlight_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      });
      this.toolManager.setUsageTracker(this.usageTracker);
    }
  }

  /**
   * Register toolbar tools (same as PageMode)
   */
  /**
   * Load (once) and register toolbar tools. Never rejects.
   */
  private ensureTools(): Promise<void> {
    if (!this.toolsPromise) {
      this.toolsPromise = this.registerTools().catch((error) => {
        this.toolsPromise = null; // allow retry on next open
        console.warn('[ReadingVox] Tools failed to load:', error);
      });
    }
    return this.toolsPromise;
  }

  private async showDictionaryAt(word: string, x: number, y: number): Promise<void> {
    try {
      if (!this.dictionary) {
        const { Dictionary } = await loadExtras();
        this.dictionary = new Dictionary((this.widget as any).config?.apiEndpoint || '/api/v1');
      }
      await this.dictionary.showPopup(word, x, y);
    } catch (error) {
      console.warn('[ReadingVox] Dictionary unavailable:', error);
    }
  }

  private async showTranslationAt(text: string, x: number, y: number): Promise<void> {
    try {
      if (!this.translation) {
        const { Translation } = await loadExtras();
        this.translation = new Translation(this.widget);
      }
      await this.translation.showPopup(text, x, y);
    } catch (error) {
      console.warn('[ReadingVox] Translation unavailable:', error);
    }
  }

  private async registerTools(): Promise<void> {
    const config = (this.widget as any).config;
    const extras = await loadExtras();
    const enabledTools = config?.enabledTools || DEFAULT_TOOLS;
    let wordHighlightWired = false;

    if (enabledTools.includes('simplify')) {
      const { SimplifyTool } = extras;
      this.toolManager.registerTool(new SimplifyTool(this.toolManager, this.toolbar));
    }
    if (enabledTools.includes('vocabSimplify')) {
      const { VocabSimplifyTool } = extras;
      this.toolManager.registerTool(new VocabSimplifyTool(this.toolManager, this.toolbar, config?.apiEndpoint || '/api/v1', this.widget.getApiClient().getAuthHeaders()));
    }
    if (enabledTools.includes('pageTheme')) {
      const { PageThemeTool } = extras;
      this.toolManager.registerTool(new PageThemeTool(this.toolManager, this.toolbar));
    }
    if (enabledTools.includes('fontSize')) {
      const { FontSizeTool } = extras;
      this.toolManager.registerTool(new FontSizeTool(this.toolManager, this.toolbar));
    }
    if (enabledTools.includes('formReader')) {
      const { FormReaderTool } = extras;
      this.toolManager.registerTool(new FormReaderTool(this.toolManager, this.toolbar, this.widget));
    }
    if (enabledTools.includes('clickListen')) {
      const { ClickListenTool } = extras;
      this.toolManager.registerTool(new ClickListenTool(this.toolManager, this.toolbar, this.widget));
    }
    if (enabledTools.includes('fontSelector')) {
      const { FontSelectorTool } = extras;
      this.toolManager.registerTool(new FontSelectorTool(this.toolManager, this.toolbar));
    }
    if (enabledTools.includes('screenMask')) {
      const { ScreenMaskTool } = extras;
      this.toolManager.registerTool(new ScreenMaskTool(this.toolManager, this.toolbar));
      if (!wordHighlightWired) {
        (this.widget as any).onWordHighlight((event: any) => {
          this.toolManager.emitWordHighlight(event);
        });
        wordHighlightWired = true;
      }
    }
    if (enabledTools.includes('magnifier')) {
      const { TextMagnifier } = extras;
      this.toolManager.registerTool(new TextMagnifier(this.toolManager, this.toolbar));
      if (!wordHighlightWired) {
        (this.widget as any).onWordHighlight((event: any) => {
          this.toolManager.emitWordHighlight(event);
        });
      }
    }
    if (enabledTools.includes('screenshotReader')) {
      const { ScreenshotReaderTool } = extras;
      const ocrTool = new ScreenshotReaderTool(
        this.toolManager,
        this.toolbar,
        (text: string) => (this.widget as any).playText(text),
      );
      this.toolManager.registerTool(ocrTool);
    }
    if (enabledTools.includes('settings')) {
      const { SettingsPanel } = extras;
      this.toolManager.registerTool(new SettingsPanel(this.toolManager, this.toolbar));
    }
  }

  /**
   * Handle text selection
   */
  private handleSelection(): void {
    setTimeout(() => {
      const selection = window.getSelection();

      if (!selection || selection.toString().trim().length === 0) {
        this.contextMenu.hide();
        return;
      }

      const text = readableSelectionText(selection);

      if (text.length < 3) {
        this.contextMenu.hide();
        return;
      }

      // Content-only presets (e.g. Canvas): only offer Listen inside content
      // areas — never in nav, sidebars, forms or editors
      if ((this.widget as any).config?.contentOnly && !this.widget.isInContent(selection.getRangeAt(0).commonAncestorContainer)) {
        this.contextMenu.hide();
        return;
      }

      this.frameSelection = null;
      this.lastSelection = {
        text,
        range: selection.getRangeAt(0)
      };

      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();

      this.contextMenu.show({
        x: rect.left + rect.width / 2,
        y: rect.top - 10,
        text
      });
    }, 10);
  }

  /** Text selected inside a readable iframe (FrameManager); empty text hides the menu */
  showFrameSelection(text: string, rect: { left: number; top: number; width: number; height: number } | null, clear: () => void): void {
    if (!text || text.length < 3 || !rect) {
      if (this.frameSelection) {
        this.frameSelection = null;
        this.contextMenu.hide();
      }
      return;
    }
    this.lastSelection = { text, range: null };
    this.frameSelection = { rect, clear };
    this.contextMenu.show({ x: rect.left + rect.width / 2, y: rect.top - 10, text });
  }

  /** Where the current selection is on screen (page or frame) */
  private selectionRect(): { left: number; bottom: number } | null {
    if (this.frameSelection) return { left: this.frameSelection.rect.left, bottom: this.frameSelection.rect.top + this.frameSelection.rect.height };
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const r = selection.getRangeAt(0).getBoundingClientRect();
      return { left: r.left, bottom: r.bottom };
    }
    return null;
  }

  /**
   * Play selected text — opens toolbar and reads the selection
   */
  private async playSelection(): Promise<void> {
    if (!this.lastSelection) return;

    this.toolbar.show();
    if (this.toggleButton) this.toggleButton.setActive(true);
    this.toolbar.setState({ isPlaying: true, progress: 0 });
    this.contextMenu.hide();

    // Clear browser selection so it doesn't cover word-by-word highlighting
    const text = this.lastSelection.text;
    window.getSelection()?.removeAllRanges();
    this.frameSelection?.clear();
    this.frameSelection = null;

    await (this.widget as any).playText(text);
  }

  /**
   * Play from the toolbar's Play button — uses last selection if available
   */
  private async playCurrentSelection(): Promise<void> {
    if (this.lastSelection) {
      await this.playSelection();
    } else {
      const sel = readableSelectionText();
      if (sel && sel.length >= 3) {
        this.lastSelection = { text: sel, range: window.getSelection()!.getRangeAt(0) };
        await this.playSelection();
      } else {
        await this.widget.play();
      }
    }
  }

  /**
   * Look up word in dictionary (from context menu)
   */
  private lookupWord(word: string): void {
    const rect = this.selectionRect();
    if (rect) {
      this.showDictionaryAt(word, rect.left, rect.bottom + 10);
    } else {
      this.showDictionaryAt(word, window.innerWidth / 2, window.innerHeight / 3);
    }
    this.contextMenu.hide();
  }

  /**
   * Translate selection (from context menu)
   */
  private translateSelection(): void {
    if (!this.lastSelection) return;

    const rect = this.selectionRect();
    if (rect) {
      this.showTranslationAt(this.lastSelection.text, rect.left, rect.bottom + 10);
    } else {
      this.showTranslationAt(this.lastSelection.text, window.innerWidth / 2, window.innerHeight / 3);
    }
    this.contextMenu.hide();
  }

  /**
   * Update player state
   */
  /** Waiting for the next sentence from the server */
  setBuffering(buffering: boolean): void {
    this.toolbar.setBuffering(buffering);
  }

  /** Open the toolbar (a placed Listen button's ⋯) */
  openToolbar(): void {
    this.toolbar.show();
    this.toggleButton?.setActive(true);
  }

  updateState(state: { isPlaying: boolean; progress: number }): void {
    this.toolbar.setState(state);
  }

  /**
   * Destroy
   */
  destroy(): void {
    document.removeEventListener('mouseup', this.boundHandleSelection);
    document.removeEventListener('touchend', this.boundHandleSelection);
    document.removeEventListener('keyup', this.boundHandleSelection);
    this.usageTracker?.destroy();
    this.toolbar.destroy();
    this.toolManager.destroy();
    this.contextMenu.destroy();
    this.toggleButton?.destroy();
    this.dictionary?.destroy();
    this.translation?.destroy();
  }
}
